import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Award, 
  Shield, 
  TrendingUp, 
  Target, 
  Clock, 
  CheckCircle,
  Zap,
  Heart
} from 'lucide-react';

const AboutPage = () => {
  const stats = [
    { value: '25+', label: 'Years Experience', icon: Clock },
    { value: '15,000+', label: 'Happy Clients', icon: Users },
    { value: '$2.8B', label: 'Assets Managed', icon: TrendingUp },
    { value: '94%', label: 'Success Rate', icon: Award }
  ];

  const values = [
    {
      icon: Zap,
      title: 'Strategic Innovation',
      description: 'We continuously evolve our investment strategies to capitalize on emerging opportunities while managing risk effectively.',
      color: 'blue'
    },
    {
      icon: Shield,
      title: 'Unwavering Integrity',
      description: 'We maintain the highest ethical standards in all our dealings, fostering transparency and trust with our clients.',
      color: 'green'
    },
    {
      icon: Heart,
      title: 'Client Partnership',
      description: 'We view each client relationship as a long-term partnership, committed to understanding your unique goals and aspirations.',
      color: 'purple'
    }
  ];

  const approach = [
    {
      step: '01',
      title: 'Comprehensive Analysis',
      description: 'We conduct thorough market analysis and due diligence to identify opportunities with optimal risk-adjusted returns.'
    },
    {
      step: '02',
      title: 'Strategic Diversification',
      description: 'We create portfolios with strategic asset allocation across multiple sectors, regions, and investment vehicles.'
    },
    {
      step: '03',
      title: 'Active Management',
      description: 'Our team continuously monitors and adjusts portfolios in response to changing market conditions and emerging opportunities.'
    },
    {
      step: '04',
      title: 'Performance Transparency',
      description: 'We provide clear, detailed reporting on portfolio performance and progress toward financial goals.'
    }
  ];

  const philosophy = [
    {
      title: 'Long-Term Perspective',
      description: 'We focus on sustainable growth rather than short-term fluctuations.',
      icon: Clock
    },
    {
      title: 'Risk Management',
      description: 'We prioritize capital preservation alongside growth opportunities.',
      icon: Shield
    },
    {
      title: 'Data-Driven Decisions',
      description: 'Our strategies are informed by rigorous research and analysis.',
      icon: TrendingUp
    },
    {
      title: 'Value Identification',
      description: 'We seek investments with intrinsic value beyond market pricing.',
      icon: Target
    }
  ];

  const valueIconTone = {
    blue: 'border border-sky-400/25 bg-sky-400/10 text-sky-100',
    green: 'border border-emerald-400/25 bg-emerald-400/10 text-emerald-100',
    purple: 'border border-violet-400/25 bg-violet-400/10 text-violet-100'
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <Navigation />

      {/* Hero Section - Simple & Clean */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(34,211,238,0.14),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(56,189,248,0.10),transparent_55%)]" />
        <div className="container mx-auto px-6 relative">
          <div className="text-center max-w-4xl mx-auto py-16 md:py-24">
            {/* Simple Badge */}
            <div className="mb-8">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold tracking-wide text-stone-200">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                About Solentra Investments
              </span>
            </div>
            
            {/* Main Title */}
            <h1 className="text-4xl md:text-6xl font-semibold mb-6 leading-[1.05] tracking-tight">
              <span className="text-white">Independent investment counsel</span>
              <br />
              <span className="text-cyan-200">for long-term wealth outcomes</span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-base md:text-xl mb-10 text-slate-300 leading-relaxed max-w-3xl mx-auto">
              We help investors structure portfolios with clear objectives, disciplined risk management, and reporting that holds up when markets move fast and stakeholders ask hard questions.
            </p>
            
            {/* Simple CTA */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <button className="w-full sm:w-auto rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors">
                  Get in touch
                </button>
              </Link>
              <Link to="/investment-plans">
                <button className="w-full sm:w-auto rounded-xl border border-white/15 bg-white/5 px-8 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors">
                  View our plans
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center rounded-3xl border border-white/10 bg-stone-950/40 p-6 card-hover">
                  <Icon className="mx-auto mb-4 text-cyan-300" size={44} />
                  <div className="text-3xl font-semibold mb-2 tabular-nums tracking-tight">{stat.value}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Our Story Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight text-white">
                Our story
              </h2>
              <p className="text-base md:text-lg text-slate-300 mb-6 leading-relaxed">
                Founded in 1999, Solentra Investments emerged from a vision to transform how individuals and businesses approach wealth management. We recognized that traditional investment models often failed to provide personalized solutions that truly aligned with each client's unique financial goals and risk tolerance.
              </p>
              <p className="text-base md:text-lg text-slate-300 mb-6 leading-relaxed">
                Our name, Solentra Investments, reflects our philosophy that each client has unique financial objectives that require tailored investment strategies, not standardized quotas or one-size-fits-all approaches.
              </p>
              <div className="flex flex-col gap-3 text-sm text-slate-400 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex items-center">
                  <CheckCircle className="text-emerald-300 mr-2" size={16} />
                  <span>Licensed & Regulated</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-emerald-300 mr-2" size={16} />
                  <span>25+ Years Experience</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl border border-white/10 bg-stone-950/40 overflow-hidden shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                  alt="Solentra Investments office"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/10 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 rounded-2xl border border-cyan-400/25 bg-cyan-400/10 px-5 py-4 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
                <div className="text-center">
                  <div className="text-2xl font-semibold text-white tabular-nums">1999</div>
                  <div className="text-xs text-cyan-100/90">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission & Values */}
      <section className="py-16 md:py-20 border-t border-white/10 bg-stone-950">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight text-white">
              Our mission & values
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              We are driven by core principles that guide our investment decisions and client relationships.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="card-glass p-8 rounded-3xl card-hover">
                  <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl mb-6 ${valueIconTone[value.color] || valueIconTone.blue}`}>
                    <Icon className="h-6 w-6" />
              </div>
                  <h3 className="text-xl font-bold text-white mb-4">{value.title}</h3>
                  <p className="text-slate-300 leading-relaxed">
                    {value.description}
              </p>
            </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Our Approach */}
      <section className="py-16 md:py-20 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight text-white">
                Our investment approach
              </h2>
              <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed">
                At Solentra Investments, we employ a disciplined investment methodology that balances proven strategies with innovative approaches tailored to each client's risk tolerance and objectives.
              </p>
              
              <div className="space-y-6">
                {approach.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10 text-sm font-semibold text-cyan-100">
                      {item.step}
                </div>
                  <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-slate-300">{item.description}</p>
                </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-glass p-8 rounded-3xl">
              <h3 className="text-2xl font-semibold text-white mb-6 tracking-tight">Investment philosophy</h3>
              <p className="text-slate-300 mb-8 leading-relaxed">
                  Our investment decisions are guided by four fundamental principles that have consistently delivered value to our clients through various market cycles:
                </p>
                
              <div className="space-y-4">
                {philosophy.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center mb-2">
                        <Icon className="text-cyan-300 mr-3" size={20} />
                        <h4 className="font-semibold text-white">{item.title}</h4>
                      </div>
                      <p className="text-slate-300 text-sm pl-8">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 md:py-20 border-t border-white/10 bg-stone-950">
        <div className="container mx-auto px-6 text-center">
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/5 p-10">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight text-white">Ready to start your journey?</h2>
            <p className="text-base md:text-lg text-slate-300 mb-8">
              Let's discuss how Solentra Investments can help you achieve your financial objectives with strategies tailored to your unique situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <button className="w-full sm:w-auto rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors">
                Schedule a consultation
              </button>
            </Link>
            <Link to="/investment-plans">
              <button className="w-full sm:w-auto rounded-xl border border-white/15 bg-white/5 px-8 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors">
                View investment plans
              </button>
            </Link>
          </div>
          </div>
        </div>
      </section>
      
      {/* Certifications & Recognitions */}
      <section className="py-16 md:py-20 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight text-white">
              Recognition & awards
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              Solentra Investments is proud to be recognized by leading financial authorities and organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-glass p-8 rounded-3xl text-center card-hover">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-400/25 bg-amber-400/10 text-sm font-semibold text-amber-100">
                FT
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Best Investment Firm 2023</h3>
              <p className="text-slate-300">Recognized by Financial Times for exceptional client service and performance.</p>
            </div>

            <div className="card-glass p-8 rounded-3xl text-center card-hover">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10 text-sm font-semibold text-cyan-100">
                5â˜…
              </div>
              <h3 className="text-xl font-bold text-white mb-4">5-Star Rating</h3>
              <p className="text-slate-300">Consistently rated 5-stars by our clients for transparency and results.</p>
            </div>

            <div className="card-glass p-8 rounded-3xl text-center card-hover">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/25 bg-emerald-400/10 text-sm font-semibold text-emerald-100">
                SEC
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Security Excellence</h3>
              <p className="text-slate-300">Awarded for maintaining the highest security standards in the industry.</p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AboutPage;